import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import axios from "axios";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { BookSession } from "@/components/book-session";
import RecentColleges from "@/components/recentpost";
import Head from "next/head";
import ErrorPage from "next/error";
import CounsellingModal from "../CounsellingModal";
import Link from "next/link";
import Container from "@/components/container";
import Header from "@/components/header";
import Layout from "@/components/layout";
import PostHeader from "@/components/post-header";
import PostBody from "@/components/post-body";
import ReviewSection from "@/components/ReviewSection";
import CollegeByLocation from "@/components/CollegeByLocation";
import TopCollegesByCutoff from "@/components/TopCollegesByCutoff";
import { useFAQToggleHandler, FAQ_TOGGLE_STYLES } from "@/components/FAQToggleHandler";
import Cookies from "js-cookie";

const normalizeValue = (value) => (value || "").toString().trim().toLowerCase();

const normalizeTag = (value) =>
  normalizeValue(value)
    .replace(/\s+/g, "-")
    .replace(/&/g, "and");

const getGhostTagSlugs = (college) => {
  if (!college?.tags || !Array.isArray(college.tags)) {
    return [];
  }

  return college.tags
    .map((tag) => tag.slug || tag.name)
    .filter(Boolean)
    .map(normalizeTag);
};

const COUNTRY_TAG_MAP = {
  china: "China",
  vietnam: "Vietnam",
  philippines: "Philippines",
  "new-zealand": "New Zealand",
  bangladesh: "Bangladesh",
  kazakhstan: "Kazakhstan",
  kyrgyzstan: "Kyrgyzstan",
  "united-states": "United States",
  "united-kingdom": "United Kingdom",
  uae: "UAE",
  luxembourg: "Luxembourg",
  japan: "Japan",
  germany: "Germany",
  netherlands: "Netherlands",
  ireland: "Ireland",
  belarus: "Belarus",
  australia: "Australia",
  canada: "Canada",
  russia: "Russia",
  georgia: "Georgia",
  uzbekistan: "Uzbekistan",
  malaysia: "Malaysia",
  nepal: "Nepal",
  hungary: "Hungary",
  romania: "Romania",
  guyana: "Guyana",
};

const getCountryFromTags = (college) => {
  const tagSlugs = getGhostTagSlugs(college);
  const matchedTag = tagSlugs.find((tagSlug) => COUNTRY_TAG_MAP[tagSlug]);
  return matchedTag ? COUNTRY_TAG_MAP[matchedTag] : "";
};

const parseCustomExcerpt = (customExcerpt) => {
  if (!customExcerpt || typeof customExcerpt !== "string") return {};

  const trimmed = customExcerpt.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
    return {};
  }

  try {
    return JSON.parse(trimmed);
  } catch (error) {
    console.warn("custom_excerpt is not valid JSON:", error);
    return {};
  }
};

const getCollegeLocation = (fetchedCollege, customExcerptData) => {
  return (
    customExcerptData.location ||
    customExcerptData.country ||
    customExcerptData.countryName ||
    customExcerptData.city ||
    getCountryFromTags(fetchedCollege) ||
    "Location Not Available"
  );
};

const getCollegeCourses = (customExcerptData) => {
  if (!customExcerptData) return ["MBBS"];
  const value = customExcerptData.courseOffered || customExcerptData.courses;
  if (Array.isArray(value) && value.length > 0) return value;
  if (typeof value === "string" && value.trim()) {
    return value.split(",").map((item) => item.trim()).filter(Boolean);
  }
  return ["MBBS"];
};

export const getServerSideProps = async (context) => {
  const { slug } = context.params;
  const baseURL = process.env.NEXT_PUBLIC_GHOST_API_URL;
  const apiKey = process.env.NEXT_PUBLIC_GHOST_API_KEY;

  // Add environment variable checks
  if (!baseURL || !apiKey) {
    console.error("Missing Ghost API URL or API Key in environment variables.");
    return { notFound: true };
  }

  try {
    const apiUrl = `${baseURL}/ghost/api/v3/content/posts/slug/${slug}/?key=${apiKey}&include=authors,tags`;
    // Log the URL being fetched for debugging
    console.log("Fetching college data from:", apiUrl);

    const res = await axios.get(apiUrl);
    const selectedCollege = res.data.posts[0];
    
    // Ensure primary_author is set from authors array
    if (selectedCollege && !selectedCollege.primary_author && selectedCollege.authors?.length > 0) {
      selectedCollege.primary_author = selectedCollege.authors[0];
    }

    if (selectedCollege) {
      selectedCollege.html = selectedCollege.html.replace(
        /ghost\.neetsupport\.com\/content\//g,
        "neetsupport.com/content/"
      );
      selectedCollege.html = selectedCollege.html.replace(
        /ghost\.neetsupport\.com/g,
        selectedCollege.canonical_url
      );

      // Update feature_image URL
      if (selectedCollege.feature_image) {
        selectedCollege.feature_image = selectedCollege.feature_image.replace(
          /ghost\.neetsupport\.com/g,
          "neetsupport.com"
        );
      }

      // Ensure every link opens in a new tab and remove query parameters
      const { JSDOM } = await import("jsdom");
      const dom = new JSDOM(selectedCollege.html);
      const { document } = dom.window;
      document.querySelectorAll("a").forEach((link) => {
        if (link.hasAttribute("href")) {
          const href = link.getAttribute("href");
          const cleanHref = href.split("?")[0]; // Remove everything after '?'
          link.setAttribute("href", cleanHref);

          // Open in new tab
          link.setAttribute("target", "_blank");
          link.setAttribute("rel", "noopener noreferrer");
        }
      });
      selectedCollege.html = document.body.innerHTML;

      return {
        props: {
          fetchedCollege: (({ url, ...rest }) => rest)(selectedCollege),
        },
      };
    } else {
      console.warn(`No college found for slug: ${slug}`);
      return { notFound: true };
    }
  } catch (error) {
    // Improved error logging
    if (error.response) {
      console.error(
        `Error fetching college data: Status ${error.response.status} - ${error.response.statusText}`,
        "URL:", error.config?.url,
        "Response data:", error.response.data
      );
    } else {
      console.error("Error fetching college data:", error.message);
    }
    return { notFound: true };
  }
};

const PrevArrow = ({ className, onClick }) => (
  <button
    className={`absolute left-0 z-10 transform -translate-y-1/2 top-1/2 ${className}`}
    onClick={onClick}
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-6 text-gray-700 hover:text-orange-500"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 19l-7-7 7-7"
      />
    </svg>
  </button>
);

// Custom Next Arrow for the carousel
const NextArrow = ({ className, onClick }) => (
  <button
    className={`absolute right-0 z-10 transform -translate-y-1/2 top-1/2 ${className}`}
    onClick={onClick}
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-6 text-gray-700 hover:text-orange-500"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5l7 7-7 7"
      />
    </svg>
  </button>
);

const CollegeDetails = ({ fetchedCollege }) => {
  const router = useRouter();
  const { slug } = router.query;

  const [modalOpen, setModalOpen] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const scrollTimeoutRef = useRef(null);

  // Review section state
  const [userRating, setUserRating] = useState(0);
  const [userReviews, setUserReviews] = useState([]);
  const [reviewContent, setReviewContent] = useState({ content: "" });
  const [hasVoted, setHasVoted] = useState({});
  const API_URL = process.env.NEXT_PUBLIC_API_URL;
  const isLoggedIn = !!Cookies.get("authToken");
  const userId = isLoggedIn ? Cookies.get("userId") : null;
  const userName = isLoggedIn ? Cookies.get("userName") : null;

  // Initialize FAQ toggle handler
  useFAQToggleHandler([fetchedCollege?.id]);

  useEffect(() => {
    if (fetchedCollege) {
      // URL replacements are already done on server-side
      // No need to process HTML again on client-side
    }
  }, [fetchedCollege]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setModalOpen(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, [slug]);

  // Handle scroll to trigger modal every 30 seconds while scrolling
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);

      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Set new timeout for 30 seconds
      scrollTimeoutRef.current = setTimeout(() => {
        if (!isFormSubmitted) {
          setModalOpen(true);
        }
      }, 30000);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [isFormSubmitted]);

  if (!fetchedCollege) {
    return <ErrorPage statusCode={404} />;
  }

  return (
    <Layout>
      <Container>
        <Header />
        <article>
          <Head>
            <title>
              {fetchedCollege ? `${fetchedCollege.meta_title}` : "Loading..."}
            </title>
            <link rel="shortcut icon" href="/favicon.ico" />
            <meta
              name="description"
              content={
                fetchedCollege
                  ? fetchedCollege.meta_description
                  : "Detailed information about the college is loading."
              }
            />
            <meta
              property="og:title"
              content={fetchedCollege ? fetchedCollege.title : "Loading..."}
            />
            <meta
              property="og:description"
              content={
                fetchedCollege
                  ? fetchedCollege.meta_description
                  : "Detailed information about the college is loading."
              }
            />
            <meta
              property="og:image"
              content={
                fetchedCollege ? fetchedCollege.og_image : "/default-image.jpg"
              }
            />
            <meta
              property="og:url"
              content={
                fetchedCollege
                  ? `${fetchedCollege.canonical_url}`
                  : "https://neetsupport.com"
              }
            />
            <meta name="twitter:card" content="summary_large_image" />
            <link
              rel="canonical"
              href={
                fetchedCollege
                  ? `${fetchedCollege.canonical_url}`
                  : "https://neetsupport.com"
              }
            />
            <style>{FAQ_TOGGLE_STYLES}</style>
            {fetchedCollege?.codeinjection_head && (
              <div
                dangerouslySetInnerHTML={{
                  __html: fetchedCollege.codeinjection_head,
                }}
              />
            )}
          </Head>

          <PostHeader
            title={fetchedCollege.title}
            coverImage={fetchedCollege.feature_image || "/default-image.jpg"}
            date={fetchedCollege.published_at}
            author={fetchedCollege.primary_author}
            url={fetchedCollege.canonical_url}
          />

          <PostBody content={fetchedCollege.html} />

          <ReviewSection
            userReviews={userReviews}
            setUserReviews={setUserReviews}
            userRating={userRating}
            setUserRating={setUserRating}
            reviewContent={reviewContent}
            setReviewContent={setReviewContent}
            hasVoted={hasVoted}
            setHasVoted={setHasVoted}
            API_URL={API_URL}
            postId={fetchedCollege.id}
            title={fetchedCollege.title}
            isLoggedIn={isLoggedIn}
            userId={userId}
            userName={userName}
          />

          {/* Other Colleges in Same Location */}
          {(() => {
            const parsedCustomExcerpt = parseCustomExcerpt(
              fetchedCollege.custom_excerpt
            );
            const location = getCollegeLocation(
              fetchedCollege,
              parsedCustomExcerpt
            );

            return (
              <div className="mt-12 px-4 sm:px-8 lg:px-28">
                <CollegeByLocation
                  location={location}
                  excludeSlug={fetchedCollege.slug}
                />
              </div>
            );
          })()}

          {/* Top Colleges by Cutoff */}
          <div className="mt-12 px-4 sm:px-8 lg:px-28">

            <TopCollegesByCutoff />
          </div>

          {/* Recent Updated Colleges */}
          <div onClick={() => {
            setTimeout(() => {
              if (!isFormSubmitted) {
                setModalOpen(true);
              }
            }, 3000);
          }} className="mt-12 px-4 sm:px-8 lg:px-28">

            {/* <RecentColleges
              tag="college"
              onCardClick={(slug) => router.push(`/college/${slug}`)}
              gridCols={4}
              showTitle={false}
            /> */}
          </div>

          {/* Recent Colleges Section */}
          <div onClick={() => {
            setTimeout(() => {
              if (!isFormSubmitted) {
                setModalOpen(true);
              }
            }, 3000);
          }} className="mt-12 px-4 sm:px-8 lg:px-28">
            <RecentColleges
              tag="college"
              onCardClick={(slug) => router.push(`/college/${slug}`)}
              gridCols={4}
              showTitle={false}
            />
          </div>
        </article>
      </Container>

      {/* Counselling Modal - Triggers on scroll and initial page load */}
      <CounsellingModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          // Reset scroll timeout after closing
          if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
          }
        }}
        college={fetchedCollege}
        course={getCollegeCourses(
          parseCustomExcerpt(fetchedCollege.custom_excerpt)
        )}
        location={getCollegeLocation(
          fetchedCollege,
          parseCustomExcerpt(fetchedCollege.custom_excerpt)
        )}
        onSubmit={() => {
          localStorage.setItem("counselling_form_submitted", "true");
          setIsFormSubmitted(true);
          setModalOpen(false);
          if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
          }
        }}
      />
    </Layout>
  );
};

export default CollegeDetails;
